/**
 * Optimisation des images fixes avant envoi : redimensionnement + WebP 0,85
 * (JPEG en secours). Canvas natif uniquement, aucune bibliothèque.
 * Les SVG et fichiers non-image sont renvoyés tels quels.
 */

export const AVATAR_MAX_SIDE = 512;
export const MEDIA_MAX_SIDE = 1080;
export const IMAGE_QUALITY = 0.85;

export const extensionFor = (mime: string): string =>
  mime === "image/webp" ? "webp" : mime === "image/png" ? "png" : mime === "image/svg+xml" ? "svg" : "jpg";

/** Encode un canvas en WebP, ou JPEG si le navigateur ne sait pas produire du WebP. */
export const canvasToOptimizedBlob = async (canvas: HTMLCanvasElement): Promise<Blob | null> => {
  const webp = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/webp", IMAGE_QUALITY));
  if (webp && webp.type === "image/webp") return webp;
  return new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", IMAGE_QUALITY));
};

const decode = async (blob: Blob): Promise<{ source: CanvasImageSource; width: number; height: number; close: () => void }> => {
  if (typeof createImageBitmap === "function") {
    try {
      const bmp = await createImageBitmap(blob);
      return { source: bmp, width: bmp.width, height: bmp.height, close: () => bmp.close() };
    } catch {
      // repli <img>
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("décodage impossible"));
      i.src = url;
    });
    return { source: img, width: img.naturalWidth, height: img.naturalHeight, close: () => URL.revokeObjectURL(url) };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
};

/** Renvoie un File optimisé (WebP/JPEG), ou la source si inutile de convertir. */
export const optimizeImage = async (
  input: File | Blob,
  { maxSide = MEDIA_MAX_SIDE, baseName = "image" }: { maxSide?: number; baseName?: string } = {},
): Promise<File> => {
  const type = input.type || "";
  const asFile = (b: Blob, name: string) =>
    input instanceof File && b === input ? input : new File([b], name, { type: b.type });

  if (!type.startsWith("image/") || type === "image/svg+xml") {
    return asFile(input, `${baseName}.${extensionFor(type)}`);
  }

  const decoded = await decode(input);
  try {
    const scale = Math.min(1, maxSide / Math.max(decoded.width, decoded.height));
    const width = Math.max(1, Math.round(decoded.width * scale));
    const height = Math.max(1, Math.round(decoded.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("canvas indisponible");
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(decoded.source, 0, 0, width, height);
    const out = await canvasToOptimizedBlob(canvas);
    if (!out) throw new Error("encodage impossible");

    // Source déjà WebP, non redimensionnée et plus légère : on la garde.
    if (type === "image/webp" && scale === 1 && input.size <= out.size) {
      return asFile(input, `${baseName}.webp`);
    }
    return new File([out], `${baseName}.${extensionFor(out.type)}`, { type: out.type });
  } finally {
    decoded.close();
  }
};

export const IMAGE_PREP_ERROR = "Impossible de préparer l'image. Essayez une autre photo.";
